import { z } from 'zod';
import { HEX_COLOR_RE, type TemplateDoc } from './template';

/**
 * Brand kit: colours, fonts and logos set once per brand. Templates refer to slots, not values:
 * `brand:primary|#4F46E5` — the slot and a fallback used when no brand (or no value for the
 * slot) is chosen. Every brand has the same slots, so one template works with any client's
 * brand; the render picks the brand (API `brand_id`, the automation, or the template's own).
 */

export const BRAND_COLOR_SLOTS = ['primary', 'secondary', 'accent', 'background', 'text'] as const;
export const BRAND_FONT_SLOTS = ['font-heading', 'font-body'] as const;
export const BRAND_LOGO_SLOTS = ['logo', 'logo-light', 'logo-icon'] as const;
export type BrandColorSlot = (typeof BRAND_COLOR_SLOTS)[number];
export type BrandFontSlot = (typeof BRAND_FONT_SLOTS)[number];
export type BrandLogoSlot = (typeof BRAND_LOGO_SLOTS)[number];
export type BrandSlot = BrandColorSlot | BrandFontSlot | BrandLogoSlot;

const hex = z.string().trim().regex(HEX_COLOR_RE, 'Expected a color like #RRGGBB');

export const brandKitSchema = z.object({
  colors: z.partialRecord(z.enum(BRAND_COLOR_SLOTS), hex).default({}),
  fonts: z
    .partialRecord(z.enum(BRAND_FONT_SLOTS), z.object({ family: z.string().min(1).max(128) }))
    .default({}),
  logos: z.partialRecord(z.enum(BRAND_LOGO_SLOTS), z.string().max(4096)).default({}),
});
export type BrandKit = z.infer<typeof brandKitSchema>;

export const emptyBrandKit = (): BrandKit => ({ colors: {}, fonts: {}, logos: {} });

const TOKEN_RE = /^brand:([a-z-]+)\|([\s\S]*)$/;

export const brandToken = (slot: BrandSlot, fallback: string) => `brand:${slot}|${fallback}`;

export function parseBrandToken(value: unknown): { slot: string; fallback: string } | null {
  if (typeof value !== 'string' || !value.startsWith('brand:')) return null;
  const m = TOKEN_RE.exec(value);
  return m ? { slot: m[1], fallback: m[2] } : null;
}

export const isBrandToken = (value: unknown) => parseBrandToken(value) !== null;

/** Value of a slot in a brand, or undefined. */
export function brandValue(kit: BrandKit | null | undefined, slot: string): string | undefined {
  if (!kit) return undefined;
  if ((BRAND_COLOR_SLOTS as readonly string[]).includes(slot))
    return kit.colors[slot as BrandColorSlot];
  if ((BRAND_FONT_SLOTS as readonly string[]).includes(slot))
    return kit.fonts[slot as BrandFontSlot]?.family;
  if ((BRAND_LOGO_SLOTS as readonly string[]).includes(slot))
    return kit.logos[slot as BrandLogoSlot] || undefined;
  return undefined;
}

export function resolveBrandValue(value: string, kit: BrandKit | null | undefined): string {
  const token = parseBrandToken(value);
  if (!token) return value;
  return brandValue(kit, token.slot) ?? token.fallback;
}

function mapStrings<T>(value: T, fn: (s: string) => string): T {
  if (typeof value === 'string') return fn(value) as T;
  if (Array.isArray(value)) return value.map((v) => mapStrings(v, fn)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = mapStrings(v, fn);
    return out as T;
  }
  return value;
}

/** The document with every brand reference replaced by the brand's value (or its fallback). */
export function applyBrand(doc: TemplateDoc, kit: BrandKit | null | undefined): TemplateDoc {
  if (!JSON.stringify(doc).includes('"brand:')) return doc;
  return mapStrings(doc, (s) => (s.startsWith('brand:') ? resolveBrandValue(s, kit) : s));
}

/** Does the document use the brand kit at all? */
export const usesBrand = (doc: TemplateDoc) => JSON.stringify(doc).includes('"brand:');
